var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Interval } from '@shared/utils/History/Interval';
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { Period } from '../IndicatorEnums';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
import { InputParameterBoolean } from '../InputParamaterClasses/InputParameterBoolean';
import { HistoricalDataRequest } from '../HistoricalDataRequest';
import { BarData } from '../HistoricalData';
var HistoryStatus;
(function (HistoryStatus) {
    HistoryStatus[HistoryStatus["Invalid"] = -1] = "Invalid";
    HistoryStatus[HistoryStatus["None"] = 0] = "None";
    HistoryStatus[HistoryStatus["Loading"] = 1] = "Loading";
    HistoryStatus[HistoryStatus["Processed"] = 2] = "Processed";
})(HistoryStatus || (HistoryStatus = {}));
export class YHL extends IndicatorScriptBase {
    constructor() {
        super();
        this.dayBorder = false;
        this.yhlHistoryStatus = HistoryStatus.None;
        this.indicatorColorHigh = '#6CFFAE';
        this.indicatorColorLow = '#3AA7FF';
        this.ProjectName = 'YHL';
        this.Comments = "Yesterday's High Low";
        this.SetIndicatorLine('BackwardHigh', this.indicatorColorHigh, 2, LineStyle.Ladder);
        this.SetIndicatorLine('BackwardLow', this.indicatorColorLow, 2, LineStyle.Ladder);
        this.historyDays = 3;
        super.InputParameter(new InputParameterInteger('historyDays', "Day's back", 0, 1, 15));
        this.applyProjection = false;
        super.InputParameter(new InputParameterBoolean('applyProjection', 'Apply projection?', 1));
        this.SeparateWindow = false;
    }
    GetIndicatorShortName() {
        return `YHL (${this.historyDays})`;
    }
    Init() {
        super.Init();
    }
    RefreshPromise() {
        const _super = Object.create(null, {
            RefreshPromise: { get: () => super.RefreshPromise }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.RefreshPromise.call(this);
            this.yhlHistoryStatus = HistoryStatus.None;
            this.yhlHistoricalRequest = new HistoricalDataRequest({
                Instrument: this.CurrentData.Instrument,
                DataType: this.CurrentData.DataType,
                Period: Period.Day,
                PeriodValue: 1
            });
        });
    }
    LoadHistory() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.CurrentData != null && this.CurrentData.Count > 0) {
                this.yhlHistoryStatus = HistoryStatus.Loading;
                const interval = this.GetTimePeriod();
                if (interval) {
                    this.yhlHistoricalRequest.fromUTC = interval.From;
                    this.yhlHistoricalRequest.toUTC = interval.To;
                    this.dayData = yield this.GetHistoricalDataPromise(this.yhlHistoricalRequest);
                    this.ProcessHistory();
                }
                else {
                    this.yhlHistoryStatus = HistoryStatus.Invalid;
                }
            }
        });
    }
    ProcessHistory() {
        if (this.dayData instanceof BarData) {
            for (let i = 0; i < this.CurrentData.Count; i++) {
                this.FindHighLow(i);
            }
        }
        this.yhlHistoryStatus = HistoryStatus.Processed;
    }
    GetTimePeriod() {
        const rightDate = this.CurrentData.getToTimeForRequest().getTime();
        const from = new Date(rightDate - (this.historyDays + 1) * 24 * 60 * 60 * 1000);
        from.setHours(0, 0, 0, 0);
        const to = new Date(rightDate + 24 * 60 * 60 * 1000);
        to.setHours(0, 0, 0, 0);
        return new Interval(from, to);
    }
    GetProjectionTimePeriod() {
        const from = new Date();
        from.setHours(0, 0, 0, 0);
        const to = new Date(from);
        to.setDate(to.getDate() + 1);
        from.setHours(0, 0, 0, 0);
        return new Interval(from, to);
    }
    OnQuote() {
        super.OnQuote();
        switch (this.yhlHistoryStatus) {
            case HistoryStatus.Invalid:
                break;
            case HistoryStatus.None:
                void this.LoadHistory();
                break;
            case HistoryStatus.Processed:
                if (this.dayData.Time(0).getDate() < this.CurrentData.Time(0).getDate()) {
                    this.FindHighLow(0);
                }
                break;
        }
    }
    FindHighLow(barIndex) {
        const intervalDateTime = this.CurrentData.Time(barIndex);
        const offset = this.dayData.FindInterval(intervalDateTime.getTime());
        const dayOffset = this.dayData.Count - 1 - offset;
        if (dayOffset === -1) {
            return;
        }
        else if (!this.dayBorder && dayOffset === 1) {
            this.dayBorder = true;
        }
        if (this.dayBorder && this.dayData.Count - 1 >= dayOffset) {
            const high = this.dayData.getPriceByType(PriceType.High, dayOffset);
            const low = this.dayData.getPriceByType(PriceType.Low, dayOffset);
            this.SetValue(0, barIndex, high);
            this.SetValue(1, barIndex, low);
        }
    }
    onPaintChart(chart, params) {
        if (!this.applyProjection || this.yhlHistoryStatus !== HistoryStatus.Processed) {
            return;
        }
        if (this.FValuesTable['line1.Color'] !== this.indicatorColorHigh) {
            this.indicatorColorHigh = this.FValuesTable['line1.Color'];
        }
        if (this.FValuesTable['line2.Color'] !== this.indicatorColorLow) {
            this.indicatorColorLow = this.FValuesTable['line2.Colorr'];
        }
        const projInterval = this.GetProjectionTimePeriod();
        const startTime = projInterval.From.getTime();
        const endTime = projInterval.To.getTime();
        if (this.dayData.Count - 1 >= 1) {
            const high = this.dayData.getPriceByType(PriceType.High, 1);
            const low = this.dayData.getPriceByType(PriceType.Low, 1);
            const startX = params.window.PointsConverter.GetScreenXbyTime(startTime);
            const endX = params.window.PointsConverter.GetScreenXbyTime(endTime);
            const highY = params.window.PointsConverter.GetScreenY(high);
            const lowY = params.window.PointsConverter.GetScreenY(low);
            params.gr.lineWidth = 2;
            params.gr.setLineDash([3, 2]);
            params.gr.strokeStyle = this.indicatorColorHigh;
            params.gr.beginPath();
            params.gr.moveTo(startX, highY);
            params.gr.lineTo(endX, highY);
            params.gr.stroke();
            params.gr.strokeStyle = this.indicatorColorLow;
            params.gr.beginPath();
            params.gr.moveTo(startX, lowY);
            params.gr.lineTo(endX, lowY);
            params.gr.stroke();
            params.gr.setLineDash([]);
        }
    }
}
